import { type FC, useContext, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { useQuery } from "@apollo/client/react";

import { MeDocument } from "@app_schema";

import storage from "@application/utilities/storage";

import { DashboardLayout } from "@layouts/dashboard_layout";

import { Loader } from "@styled/loader";

import { World } from "@application/contexts/world";

export const DashboardApp: FC = () => {
  const { authenticated, login } = useContext(World);
  const location = useLocation();

  // Password / invite / signup set the `authenticated` flag synchronously, so
  // those sessions take the fast path. SSO redirects in with a valid session
  // cookie but never runs client code, so the flag is absent — fall back to the
  // session itself (the Me cache ME_LOADER already primed) as the source of truth.
  const { data, loading } = useQuery(MeDocument, { skip: authenticated });
  const me = data?.me;

  // Seed the org header in render (NOT a useEffect) on purpose: oid must be set
  // before <Outlet /> mounts so org-scoped child queries carry X-Organization-Id
  // on their first request. Idempotent (guarded by !oid). Do not move into an
  // effect — that runs after children mount and reintroduces the ordering bug.
  if (me && !storage.get("oid")) {
    const organization =
      me.organizations.find((org) => org.current) ?? me.organizations[0];
    if (organization) storage.set("oid", organization.id);
  }

  // Sync the client flag to the live session so later navigations hit the fast path.
  useEffect(() => {
    if (me && !authenticated) login();
  }, [me, authenticated, login]);

  if (authenticated || me) {
    return (
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    );
  }

  if (loading) return <Loader />;

  return <Navigate to={"/login"} state={{ location }} />;
};
